// The portal language picker (PHASE26): on the sign-in page it only changes this browser; signed in, it is also
// saved as the user's own language. It never changes what callers hear (the voice language is set per number
// and IVR option).
import { Languages } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import i18n, { LANGUAGES, setLanguage, t } from '@/i18n';
import { api, errorText } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { toast } from '@/store/toast';
import { cn } from '@/lib/utils';

export function LanguageMenu({ className }: { className?: string }) {
  const signedIn = useAuth((s) => !!s.token);
  const qc = useQueryClient();
  async function change(lang: string) {
    if (signedIn) {
      try { await api('/auth/me/language', { method: 'PUT', body: { language: lang } }); }
      catch (e) { toast.error(errorText(e)); return; }
      // the cached /auth/me answers carry the language too: keep them in step, or the portal switches back
      for (const key of ['me', 'me-branding']) qc.setQueriesData({ queryKey: [key] }, (d: unknown) => (d && typeof d === 'object' ? { ...d, language: lang } : d));
    }
    setLanguage(lang);
  }
  return (
    <label className={cn('flex items-center gap-1 text-sm', className)} title={t('Portal language')}>
      <Languages className="size-4 text-muted-foreground" aria-hidden />
      <select data-testid="language-menu" aria-label={t('Portal language')} value={i18n.language} onChange={(e) => change(e.target.value)}
        className="rounded-md border bg-background px-1 py-0.5 text-sm">
        {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </label>
  );
}

// after /auth/me: show the portal in the user's language (their own, else the tenant's default)
export function applyUserLanguage(me: { language?: string | null } | undefined): void {
  if (me?.language) setLanguage(me.language);
}
